import {
	isDimensionFilterCardData,
	type DimensionFilterCardData,
} from '../../../../models/page-filter.model';
import {
	parseCardPreviewDataFetchKey,
	previewCardDataQueryFingerprint,
} from '../../../../utils/card-preview-data-fetch-key.util';
import {
	buildPlaceholderDimensionFilterData,
	mergeDimensionFilterPreviewData,
} from '../../../../utils/dimension-filter-merge.util';
import { extractRawCardDataFromGql } from '../../jepto-table-editor/services/table-preview-data.util';

export { extractRawCardDataFromGql, mergeDimensionFilterPreviewData };

/** True when WebSocket / inline `getCardData` returned dropdown options to pick from. */
export function hasRenderableDimensionFilterPayload(
	data: unknown
): data is DimensionFilterCardData {
	return (
		isDimensionFilterCardData(data) && data.options.length > 0
	);
}

export function dimensionFilterDataFingerprint(
	data: DimensionFilterCardData | undefined
): string {
	if (!hasRenderableDimensionFilterPayload(data)) {
		return '';
	}
	return JSON.stringify({
		member: data.dimension.member,
		options: data.options.map(o => `${o.value}:${o.label}`),
		// Include selection — layout skips setInput when fingerprint matches, so
		// omitting selected left the card stuck on stale "all selected" after
		// the user unchecks an option (session selection never reached the input).
		selected: [...data.selected].sort(),
	});
}

/** True when hydrated filter preview data matches the editor fetch key. */
export function cachedDimensionFilterMatchesFetchKey(
	fetchKey: string,
	config: { cardId: string; data?: Record<string, unknown> },
	cached: unknown
): boolean {
	if (!hasRenderableDimensionFilterPayload(cached)) {
		return false;
	}
	const parsed = parseCardPreviewDataFetchKey(fetchKey);
	if (!parsed) {
		return false;
	}
	if (
		parsed.queryFingerprint !==
		previewCardDataQueryFingerprint(config.data)
	) {
		return false;
	}
	const configMember = (
		config.data?.['dimensions'] as Array<{ name?: string }> | undefined
	)?.[0]?.name;
	return !configMember || cached.dimension.member === configMember;
}

export function parseDimensionFilterCardData(
	raw: unknown
): DimensionFilterCardData | null {
	if (typeof raw === 'string') {
		try {
			return parseDimensionFilterCardData(JSON.parse(raw) as unknown);
		} catch {
			return null;
		}
	}
	if (!isDimensionFilterCardData(raw)) {
		return null;
	}
	return raw;
}

export { buildPlaceholderDimensionFilterData };
